"use client";

import React, { useState } from 'react';
import { Shield, Smartphone, Key, Eye, EyeOff, CheckCircle, AlertTriangle, RefreshCw, Copy,  } from 'lucide-react';
import { useLanguage } from '@/lib/language-context';
import { t } from '@/lib/translations';
import { useAppToast } from '@/lib/use-app-toast';
import { auditLog } from '@/lib/supabase/audit-logger';

const backupCodes = [
  'OTEK-A8F2-D1C3',
  'OTEK-B9G3-E2D4',
  'OTEK-C1H4-F3E5',
  'OTEK-D2I5-G4F6',
  'OTEK-E3J6-H5G7',
  'OTEK-F4K7-I6H8',
  'OTEK-G5L8-J7I9',
  'OTEK-H6M9-K8J0',
];

export default function SecurityMFASettings() {
  const { language } = useLanguage();
  const [mfaEnabled, setMfaEnabled] = useState(true);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [sessionTimeout, setSessionTimeout] = useState('30');
  const { success, error: toastError } = useAppToast();

  const handleMFAToggle = async () => {
    if (mfaEnabled) {
      toastError('MFA cannot be disabled — required by BD Compliance Act 2023 §11', 'Contact your administrator to modify this policy.');
      await auditLog.securitySettingsUpdated();
      return;
    }
    setIsVerifying(true);
    await new Promise(r => setTimeout(r, 1000));
    setMfaEnabled(true);
    setIsVerifying(false);
    await auditLog.securitySettingsUpdated();
    success('MFA enabled successfully');
  };

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    success('Backup code copied');
  };

  const handleCopyAll = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    success('All backup codes copied');
  };

  return (
    <div className="space-y-5">
      {/* MFA Status Card */}
      <div className={`cyber-card p-5 ${mfaEnabled ? 'border-green-500/20' : 'border-red-500/20'}`}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
              mfaEnabled ? 'bg-green-500/15 border border-green-500/25' : 'bg-red-500/15 border border-red-500/25'
            }`}>
              <Shield size={18} className={mfaEnabled ? 'text-cyber-green' : 'text-cyber-red'} />
            </div>
            <div>
              <h2 className={`text-sm font-semibold text-white ${language === 'bn' ? 'font-bangla' : ''}`}>
                {t(language, 'settings_mfa_title')}
              </h2>
              <p className={`text-[11px] text-cyber-muted mt-0.5 ${language === 'bn' ? 'font-bangla' : ''}`}>
                {t(language, 'settings_mfa_desc')}
              </p>
            </div>
          </div>

          {/* Toggle */}
          <button
            onClick={handleMFAToggle}
            disabled={isVerifying}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none ${
              mfaEnabled ? 'bg-cyber-green' : 'bg-cyber-border'
            }`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition-transform duration-200 ${
              mfaEnabled ? 'translate-x-6' : 'translate-x-1'
            }`} />
          </button>
        </div>

        {/* Status Badge */}
        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium ${
          mfaEnabled
            ? 'bg-green-500/10 border-green-500/25 text-cyber-green' :'bg-red-500/10 border-red-500/25 text-cyber-red'
        }`}>
          {mfaEnabled ? <CheckCircle size={13} /> : <AlertTriangle size={13} />}
          <span className={language === 'bn' ? 'font-bangla' : ''}>
            {mfaEnabled ? t(language, 'settings_mfa_enabled') : t(language, 'settings_mfa_disabled')}
          </span>
          {mfaEnabled && (
            <span className="ml-auto text-[10px] font-mono text-cyber-muted">
              Last verified: 17-03-2026 04:08 BDT
            </span>
          )}
        </div>
      </div>

      {/* Authenticator App */}
      <div className="cyber-card p-5">
        <div className="flex items-center gap-2 mb-4">
          <Smartphone size={15} className="text-cyber-blue" />
          <h3 className="text-sm font-semibold text-white">Authenticator App</h3>
          <span className="text-[10px] font-mono bg-cyber-blue/10 text-cyber-blue border border-cyber-blue/20 rounded-full px-2 py-0.5">
            Google Authenticator / Authy
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* QR Code */}
          <div>
            <p className="text-xs text-cyber-muted mb-3">Scan QR code to link your authenticator app</p>
            {showQR ? (
              <div className="bg-white p-4 rounded-xl w-fit">
                {/* QR Code Placeholder SVG */}
                <svg width="120" height="120" viewBox="0 0 120 120" fill="none">
                  <rect width="120" height="120" fill="white" />
                  {/* QR pattern simulation */}
                  {Array.from({ length: 9 }, (_, row) =>
                    Array.from({ length: 9 }, (_, col) => {
                      const isDark = (row + col) % 2 === 0 || (row === 0 && col < 7) || (row === 6 && col < 7) || (col === 0 && row < 7) || (col === 6 && row < 7);
                      return isDark ? (
                        <rect key={`${row}-${col}`} x={col * 13 + 2} y={row * 13 + 2} width="11" height="11" fill="#0D0D0D" rx="1" />
                      ) : null;
                    })
                  )}
                </svg>
                <p className="text-[9px] text-gray-500 mt-2 text-center font-mono">otpauth://totp/Otek1-Syber:r.alam@btrc.gov.bd</p>
              </div>
            ) : (
              <div
                onClick={() => setShowQR(true)}
                className="w-28 h-28 bg-[#131620] border-2 border-dashed border-cyber-border rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-cyber-blue/40 transition-colors group"
              >
                <Shield size={24} className="text-cyber-subtle group-hover:text-cyber-blue transition-colors mb-1" />
                <span className="text-[10px] text-cyber-muted group-hover:text-cyber-blue transition-colors">Show QR</span>
              </div>
            )}
          </div>

          {/* Manual Entry */}
          <div>
            <p className="text-xs text-cyber-muted mb-3">Or enter this secret key manually</p>
            <div className="bg-[#131620] border border-cyber-border rounded-lg px-3 py-2.5 flex items-center justify-between gap-2">
              <span className="font-mono text-[12px] text-cyber-blue tracking-widest">
                {showQR ? 'JBSWY3DPEHPK3PXP' : '••••••••••••••••'}
              </span>
              <button
                onClick={() => {
                  navigator.clipboard.writeText('JBSWY3DPEHPK3PXP');
                  success('Secret key copied');
                }}
                className="text-cyber-muted hover:text-cyber-blue transition-colors"
              >
                <Copy size={13} />
              </button>
            </div>
            <p className="text-[10px] text-cyber-subtle mt-2 font-mono">
              Algorithm: TOTP · Digits: 6 · Period: 30s
            </p>
          </div>
        </div>
      </div>

      {/* Backup Codes */}
      <div className="cyber-card p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Key size={15} className="text-cyber-yellow" />
            <h3 className={`text-sm font-semibold text-white ${language === 'bn' ? 'font-bangla' : ''}`}>
              {t(language, 'settings_mfa_backup')}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleCopyAll}
              className="flex items-center gap-1.5 text-[11px] text-cyber-muted hover:text-cyber-blue transition-colors"
            >
              <Copy size={11} />
              Copy All
            </button>
            <button
              onClick={() => setShowBackupCodes(!showBackupCodes)}
              className="flex items-center gap-1.5 text-[11px] text-cyber-muted hover:text-cyber-text transition-colors"
            >
              {showBackupCodes ? <EyeOff size={11} /> : <Eye size={11} />}
              {showBackupCodes ? 'Hide' : 'Show'}
            </button>
          </div>
        </div>

        <div className="bg-[#0A0D14] border border-cyber-border rounded-lg p-4">
          <div className="grid grid-cols-2 gap-2">
            {backupCodes.map((code, i) => (
              <div
                key={i}
                className="flex items-center justify-between px-2.5 py-1.5 bg-[#131620] border border-cyber-border/50 rounded-md group hover:border-cyber-blue/25 transition-colors"
              >
                <span className="font-mono text-[11px] text-cyber-text">
                  {showBackupCodes ? code : '••••-••••-••••'}
                </span>
                <button
                  onClick={() => handleCopyCode(code)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity text-cyber-muted hover:text-cyber-blue"
                >
                  <Copy size={10} />
                </button>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-cyber-muted mt-3 font-mono">
            ⚠️ Each code can only be used once. Store securely — not in email or cloud.
          </p>
        </div>

        <button className="mt-3 flex items-center gap-1.5 text-xs text-cyber-muted hover:text-yellow-400 transition-colors">
          <RefreshCw size={12} />
          Regenerate backup codes
        </button>
      </div>

      {/* Session Settings */}
      <div className="cyber-card p-5">
        <h3 className="text-sm font-semibold text-white mb-4">Session Security</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-cyber-text">Session Timeout</p>
              <p className="text-[11px] text-cyber-muted font-mono">Auto-logout after inactivity</p>
            </div>
            <select
              value={sessionTimeout}
              onChange={(e) => setSessionTimeout(e.target.value)}
              className="bg-[#131620] border border-cyber-border rounded-lg px-3 py-1.5 text-xs text-cyber-text font-mono outline-none focus:border-cyber-blue/50 transition-colors cursor-pointer"
            >
              <option value="15">15 minutes</option>
              <option value="30">30 minutes</option>
              <option value="60">1 hour</option>
              <option value="240">4 hours</option>
            </select>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-cyber-text">Force Re-authentication</p>
              <p className="text-[11px] text-cyber-muted font-mono">Require MFA for sensitive actions</p>
            </div>
            <div className="relative inline-flex h-6 w-11 items-center rounded-full bg-cyber-green cursor-pointer">
              <span className="inline-block h-4 w-4 transform rounded-full bg-white shadow-md translate-x-6 transition-transform" />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-cyber-text">Login Notifications</p>
              <p className="text-[11px] text-cyber-muted font-mono">Email alert on each login</p>
            </div>
            <div className="relative inline-flex h-6 w-11 items-center rounded-full bg-cyber-green cursor-pointer">
              <span className="inline-block h-4 w-4 transform rounded-full bg-white shadow-md translate-x-6 transition-transform" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}